import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { subscriptionApi } from '../lib/subscriptionApi';
import { useAuth } from './AuthContext';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  billing_cycle: 'monthly' | 'annual';
  stripe_price_id?: string;
  services: string[];
  max_active_requests: number;
}

interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing';
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  plan?: SubscriptionPlan;
}

interface SubscriptionContextType {
  subscription: UserSubscription | null;
  plans: SubscriptionPlan[];
  loading: boolean;
  error: string | null;
  hasActiveSubscription: boolean;
  canCreateRequest: boolean;
  activeRequestsCount: number;
  maxActiveRequests: number;
  refreshSubscription: () => Promise<void>;
  createSubscription: (planId: string, planName: string, billingCycle: 'monthly' | 'annual', price: number) => Promise<void>;
  cancelSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeRequestsCount, setActiveRequestsCount] = useState(0);
  const [userLoaded, setUserLoaded] = useState(false);

  const fetchSubscription = async () => {
    if (!user) {
      console.log('No user, skipping subscription fetch');
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching subscription for user:', user.id);
      setError(null);
      setLoading(true);
      
      // Fetch user's subscription with plan details
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          plan:subscription_plans(*)
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        throw subscriptionError;
      }

      console.log('Subscription data:', subscriptionData);
      setSubscription(subscriptionData);

      // Fetch active requests count
      const { count: requestsCount } = await supabase
        .from('service_requests')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .in('status', ['pending', 'in_progress', 'review']);

      setActiveRequestsCount(requestsCount || 0);

    } catch (error: any) {
      setError(error.message);
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      console.log('Fetching subscription plans...');
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price', { ascending: true });

      if (error) {
        console.error('Error fetching plans:', error);
        throw error;
      }
      
      console.log('Plans fetched successfully:', data);
      setPlans(data || []);
    } catch (error: any) {
      console.error('Error fetching plans:', error);
      setError(`Failed to load plans: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    console.log('SubscriptionContext - authLoading:', authLoading, 'user:', user?.id);
    
    // Only proceed if auth is not loading and we have a user
    if (!authLoading && user) {
      console.log('User loaded in SubscriptionContext:', user.id);
      setUserLoaded(true);
      
      // Add a small delay to ensure auth state is fully settled
      const timer = setTimeout(() => {
        fetchSubscription();
      }, 100);
      
      return () => clearTimeout(timer);
    } else if (!authLoading && !user) {
      console.log('No user in SubscriptionContext after auth loaded');
      setUserLoaded(false);
      setSubscription(null);
      setLoading(false);
    }
    // If authLoading is true, we don't do anything yet - wait for it to complete
  }, [user, authLoading]);

  const hasActiveSubscription = Boolean(
    subscription && 
    subscription.status === 'active' && 
    (!subscription.current_period_end || new Date(subscription.current_period_end) > new Date())
  );

  const maxActiveRequests = subscription?.plan?.max_active_requests || 0;
  const canCreateRequest = hasActiveSubscription && activeRequestsCount < maxActiveRequests;

  const refreshSubscription = async () => {
    await fetchSubscription();
  };

  const createSubscription = async (planId: string, planName: string, billingCycle: 'monthly' | 'annual', price: number) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setError(null);
      
      const result = await subscriptionApi.createSubscription({
        planId,
        userId: user.id,
        planName,
        billingCycle,
        price,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create subscription');
      }

      // The user will be redirected to Stripe checkout
      // The subscription will be updated via webhook or success page
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const cancelSubscription = async () => {
    if (!subscription) throw new Error('No active subscription');

    try {
      setError(null);
      
      const result = await subscriptionApi.cancelSubscription(subscription.id);

      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel subscription');
      }

      await refreshSubscription();
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    subscription,
    plans,
    loading,
    error,
    hasActiveSubscription,
    canCreateRequest,
    activeRequestsCount,
    maxActiveRequests,
    refreshSubscription,
    createSubscription,
    cancelSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 